import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import FormGenerator from 'app/containers/Designer/Form/components/FormGenerator';
import { Typography } from '@mic3/platform-ui';
import { shallowEquals } from 'app/utils/utils';
import { updateMapLayer } from 'store/actions/maps/situationalAwarenessActions';
import { LAYERS, __getMapLayers } from 'app/utils/maps/layer/layerUtils';
import Icon from 'app/components/atoms/Icon/Icon';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import moment from 'moment';
import styled from 'styled-components';

const Container = styled.div`
    max-width: 960px;
    margin: auto;
`;

const FormGeneratorStyled = styled(FormGenerator)`
    .LabelBox {
        padding: 0;
    }
`;

const ExpansionPanelStyled = styled(ExpansionPanel)`
    &.MuiExpansionPanel-rounded:first-child {
        border-top-left-radius: 0;
        border-top-right-radius: 0;
    }

    &.MuiExpansionPanel-rounded:last-child {
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
    }
`;

function getTimeRangePayload(time_range) {
    const isRelativeTime = time_range?.amount || time_range?.unit || time_range?.range;

    let startTime, endTime;
    if (isRelativeTime) {
        const { amount, unit, range } = time_range;
        startTime = moment();
        endTime = moment();

        if (range === 'add') {
            endTime = endTime.add(amount, unit);
        } else if (range === 'subtract') {
            startTime = startTime.subtract(amount, unit);
        }

        return {
            0: startTime.toISOString(),
            1: endTime.toISOString(),
            amount: time_range.amount,
            range: time_range.range,
            relative: time_range.relative,
            unit: time_range.unit,
        };
    } else {
        startTime = moment(time_range[0]);
        endTime = moment(time_range[1]);

        return [startTime.toISOString(), endTime.toISOString()];
    }
}

function ReplayLayerAbout(props) {
    const { id, details, title, setActions, setStatus} = props;

    const dispatch = useDispatch();
    const formRef = useRef();
    const allMapLayers = useSelector(state => __getMapLayers(state.maps.situationalAwareness.map.data));
    const correctLayerData = allMapLayers.find(layer => layer.id === id);

    const [formState, setFormState] = useState({
        layerForm: { ...correctLayerData },
        initialForm: { ...correctLayerData },
        formChanged: false,
    });

    useEffect(() => {
        if (correctLayerData?.name) {  
            setFormState(prev => ({
                ...prev,
                layerForm: {
                    ...prev.layerForm,
                    name: correctLayerData.name,  
                },
                initialForm: {
                    ...prev.initialForm,
                    name: correctLayerData.name, 
                }
            }));
        }
    }, [correctLayerData]); 

    const onFormSubmit = useCallback(async () => {
        const { data, errors } = await formRef.current.isValidForm();
        if (!errors) {
            const { description, time_range, relatedEntities = [] } = data;

            const { type } = details || '';
            const foundLayer = allMapLayers.find(layer => layer.id === id);
            const timeRangePayload = getTimeRangePayload(time_range);    

            const newRelatedEntities = Array.isArray(relatedEntities)
                ? relatedEntities.map(entity => ({
                    id: entity.id,
                    name: entity.name,
                    type: entity.type,
                    layerId: id  
                }))
                : [];

            const updatedLayerData = {
                id,
                name: foundLayer?.name,
                type: LAYERS[type],
                description,
                time_range: timeRangePayload,
                relatedEntities: newRelatedEntities,
                shouldRestartReplay: true,
                active: true,
                visible: foundLayer?.visible ?? true,
                opacity: foundLayer?.opacity || 100,
            };

            dispatch(updateMapLayer(updatedLayerData));
            setFormState(prev => ({
                ...prev,
                initialForm: { data },
                formChanged: false,
            }));
            setStatus(false);
            setActions(null);
        }
    }, [allMapLayers, details, dispatch, id, setActions, setStatus]);

    const setActionsIfNeeded = useCallback(() => {
        if (formState.formChanged) {
            setActions(onFormSubmit);
        } else {
            setActions(null);
        }
    }, [formState.formChanged, onFormSubmit, setActions]);

    useEffect(() => {
        if (setActions) {
            setActionsIfNeeded();
        }
    }, [setActionsIfNeeded, setActions]);


    const buildFormDefinition = data => {
        return [
           
            {
                field: 'panel',
                type: 'panel',
                properties: {
                    header: 'Description',
                    expanded: false,
                },
                children: [
                    {
                        field: 'description',
                        type: 'textarea',
                        properties: {
                            label: 'Description',
                            name: 'description',
                            disabled: data?.isOld,
                        },
                    },
                ],
            },
            {
                field: 'replayInfo',
                type: 'panel',
                properties: {
                    header: 'Replay Information',
                    expanded: false,
                },
                children: [
                    {
                        type: 'dateTimeRange',
                        properties: {
                            label: 'Replay Time',
                            name: 'time_range',
                        },
                        filter: false,
                        constraints: { required: true },
                    },
                    {
                        type: 'relatedEntities',
                        properties: {
                            label: 'Replay Entities',
                            name: 'relatedEntities',
                            relatedEntityTypeLabel: 'Entity Type',
                            relatedEntitiesLabel: 'Entities',
                        },
                        condition: 'in',
                        sort: false,
                    },
                ],
            },
        ];
    };

    const handleChange = async (data, { name, value }) => {
        setFormState(prev => ({ ...prev, layerForm: data }));

        const { initialForm, formChanged } = formState;
        if (!formChanged && !shallowEquals(value, initialForm[name])) {
            setFormState(prev => ({ ...prev, formChanged: true }));
            await setStatus(true);
            setActions(onFormSubmit);
        } else if (formChanged && shallowEquals(value, initialForm[name])) {
            setFormState(prev => ({ ...prev, formChanged: false }));
            await setStatus(false);
            setActions(null);
        }
    };

    return (
        <Container>
            <ExpansionPanelStyled style={{ margin: '0 16px' }} >
                <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Icon name="play" type="mdi" />
                        <Typography
                            style={{
                                paddingLeft: '10px',
                                fontWeight: 400,
                                fontSize: '18px',
                                lineHeight: '20px',
                            }}
                        >
                            {title}
                        </Typography>
                    </div>
                </ExpansionPanelSummary>
                <ExpansionPanelDetails>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Typography
                            style={{
                                paddingLeft: '10px',
                                fontWeight: 500,
                                fontSize: '24px',
                                lineHeight: '28px',
                            }}
                        >
                            {allMapLayers.find(layer => layer.id === id)?.name}
                        </Typography>
                    </div>
                </ExpansionPanelDetails>
            </ExpansionPanelStyled>
        
            <FormGeneratorStyled 
                data={formState.layerForm} 
                onChange={handleChange} 
                components={buildFormDefinition(formState.layerForm)} 
                ref={formRef} 
            />
        </Container>
    );
}

ReplayLayerAbout.propTypes = {
    id: PropTypes.string.isRequired,
    map: PropTypes.object.isRequired,
    mapId: PropTypes.string.isRequired,
    layer: PropTypes.object.isRequired,
    details: PropTypes.object,
    title: PropTypes.string,
    setActions: PropTypes.func,
    setStatus: PropTypes.func,
    setToDefault: PropTypes.bool,
};

export default ReplayLayerAbout;
